import React, { useState, useEffect } from 'react';
import { 
  FaCalendarDay, 
  FaCalendarWeek, 

  FaCalendarAlt,     // Correct replacement for FaCalendarMonth
  FaChartLine, 
  FaUsers, 
  FaStar 
} from 'react-icons/fa';
// import { 
//   FaCalendarDay, FaCalendarWeek, FaCalendarMonth, 
//   FaChartLine, FaUsers, FaStar 
// } from 'react-icons/fa';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts';
import { BiCalendarCheck, BiColor } from 'react-icons/bi';

const AssessmentAnalytics = ({ assessments = [] }) => {
  const [timeFilter, setTimeFilter] = useState('today'); // today, yesterday, week, month, 3months, 6months, year, lastyear
  const [filteredData, setFilteredData] = useState([]);
  const [stats, setStats] = useState({});

  const timeOptions = [
    { key: 'today', label: 'Today', icon: <FaCalendarDay /> },
    { key: 'yesterday', label: 'Yesterday', icon: <FaCalendarDay /> },
    { key: 'week', label: 'This Week', icon: <FaCalendarWeek /> },
    { key: 'month', label: 'This Month', icon: <BiCalendarCheck /> },
    { key: '3months', label: 'Last 3 Months', icon: <FaChartLine /> },
    { key: '6months', label: 'Last 6 Months', icon: <FaChartLine /> },
    { key: 'year', label: 'This Year', icon: <FaChartLine /> },
    { key: 'lastyear', label: 'Last Year', icon: <FaChartLine /> },
  ];

  useEffect(() => {
    if (!assessments.length) return;

    const now = new Date();
    let filtered = [];

    switch (timeFilter) {
      case 'today':
        filtered = assessments.filter(a => {
          const date = new Date(a.createdAt || a.evaluationDate);
          return date.toDateString() === now.toDateString();
        });
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        filtered = assessments.filter(a => {
          const date = new Date(a.createdAt || a.evaluationDate);
          return date.toDateString() === yesterday.toDateString();
        });
        break;
      case 'week':
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        filtered = assessments.filter(a => new Date(a.createdAt || a.evaluationDate) >= weekAgo);
        break;
      case 'month':
        const monthAgo = new Date(now.getFullYear(), now.getMonth(), 1);
        filtered = assessments.filter(a => new Date(a.createdAt || a.evaluationDate) >= monthAgo);
        break;
      case '3months':
        const threeMonthsAgo = new Date(now);
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        filtered = assessments.filter(a => new Date(a.createdAt || a.evaluationDate) >= threeMonthsAgo);
        break;
      case '6months':
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        filtered = assessments.filter(a => new Date(a.createdAt || a.evaluationDate) >= sixMonthsAgo);
        break;
      case 'year':
        const yearStart = new Date(now.getFullYear(), 0, 1);
        filtered = assessments.filter(a => new Date(a.createdAt || a.evaluationDate) >= yearStart);
        break;
      case 'lastyear':
        const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);
        const lastYearEnd = new Date(now.getFullYear() - 1, 11, 31);
        filtered = assessments.filter(a => {
          const d = new Date(a.createdAt || a.evaluationDate);
          return d >= lastYearStart && d <= lastYearEnd;
        });
        break;
      default:
        filtered = assessments;
    }

    setFilteredData(filtered);

    // Calculate Statistics
    const total = filtered.length;
    const excellent = filtered.filter(a => a.overallLevel === 'Excellent').length;
    const good = filtered.filter(a => a.overallLevel === 'Good').length;
    const avgScore = total > 0 
      ? (filtered.reduce((sum, a) => sum + (a.percentageScore || 0), 0) / total).toFixed(1) 
      : 0;

    setStats({
      total,
      excellent,
      good,
      avgScore,
      excellentRate: total > 0 ? ((excellent / total) * 100).toFixed(1) : 0
    });
  }, [timeFilter, assessments]);

  // Chart Data
  const levelData = [
    { name: 'Excellent', value: stats.excellent || 0, fill: '#22c55e' },
    { name: 'Good', value: stats.good || 0, fill: '#3b82f6' },
    { name: 'Fair', value: filteredData.filter(a => a.overallLevel === 'Fair').length, fill: '#eab308' },
    { name: 'Poor', value: filteredData.filter(a => a.overallLevel === 'Poor').length, fill: '#ef4444' },
  ];

  return (
    <div className="bg-white rounded-3xl shadow-xl p-8">
      <div className="flex flex-col md:flex-row justify-between items-start mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FaChartLine className="text-orange-600" /> Assessment Analytics
          </h2>
          <p className="text-gray-600 mt-1">Performance overview of staff assessments</p>
        </div>

        {/* Time Filter Buttons */}
        <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
          {timeOptions.map((option) => (
            <button
              key={option.key}
              onClick={() => setTimeFilter(option.key)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-sm font-medium transition-all ${
                timeFilter === option.key 
                  ? 'bg-orange-600 text-white shadow-lg' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              {option.icon}
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
        <div className="bg-gradient-to-br from-orange-500 to-amber-500 text-white p-6 rounded-3xl">
          <div className="flex justify-between">
            <div>
              <p className="text-orange-100 text-sm">Total Assessments</p>
              <p className="text-4xl font-bold mt-2">{stats.total}</p>
            </div>
            <FaUsers className="text-4xl opacity-80" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-6 rounded-3xl">
          <p className="text-gray-500 text-sm">Average Score</p>
          <p className="text-4xl font-bold text-gray-900 mt-2">{stats.avgScore}%</p>
          <p className="text-green-600 text-sm mt-1">↑ Performance</p>
        </div>

        <div className="bg-white border border-gray-200 p-6 rounded-3xl">
          <p className="text-gray-500 text-sm">Excellent Ratings</p>
          <p className="text-4xl font-bold text-green-600 mt-2">{stats.excellent}</p>
          <p className="text-sm text-gray-600 mt-1">{stats.excellentRate}% of total</p>
        </div>

        <div className="bg-white border border-gray-200 p-6 rounded-3xl">
          <p className="text-gray-500 text-sm">Good Ratings</p>
          <p className="text-4xl font-bold text-blue-600 mt-2">{stats.good}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Pie Chart */}
        <div className="bg-gray-50 rounded-3xl p-6">
          <h3 className="font-semibold text-lg mb-6">Assessment Levels Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={levelData}
                cx="50%"
                cy="50%"
                innerRadius={80}
                outerRadius={120}
                dataKey="value"
              >
                {levelData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 mt-4">
            {levelData.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }} />
                <span className="text-sm text-gray-600">{item.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Assessments Table */}
        <div className="bg-gray-50 rounded-3xl p-6">
          <h3 className="font-semibold text-lg mb-6 flex items-center gap-2">
            Recent Assessments
          </h3>
          <div className="overflow-x-auto max-h-80">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 text-sm font-medium text-gray-600">Candidate</th>
                  <th className="text-left py-3 text-sm font-medium text-gray-600">Date</th>
                  <th className="text-left py-3 text-sm font-medium text-gray-600">Score</th>
                  <th className="text-left py-3 text-sm font-medium text-gray-600">Level</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.slice(0, 8).map((ass, index) => (
                  <tr key={index} className="border-b last:border-none hover:bg-white">
                    <td className="py-4 font-medium">{ass.applicantName}</td>
                    <td className="py-4 text-sm text-gray-600">
                      {new Date(ass.createdAt || ass.evaluationDate).toLocaleDateString()}
                    </td>
                    <td className="py-4 font-semibold">{ass.percentageScore}%</td>
                    <td className="py-4">
                      <span className={`px-4 py-1 text-xs font-medium rounded-full ${BiColor(ass.overallLevel)}`}>
                        {ass.overallLevel}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentAnalytics;